// Zonele exterioare ale machetei de situație: PLATFORMA DE GUNOI și LOCUL DE
// JOACĂ (și piațeta, doar pavaj). Pe planșă sunt un poligon cu un simbol; pe
// machetă, până la 02.11.2026, nu erau nimic — o pată de verde — și omul nu
// avea cum să le găsească (Oana: «să arate ok, cum arată în mod normal o ghenă
// și un loc de joacă»).
//
// Fiecare zonă se așază pe DREPTUNGHIUL EI de arie minimă (`cadrulZonei`): o
// ghenă are un perete în spate și o deschidere în față, un loc de joacă un
// gard cu o poartă, iar «față» și «poartă» sunt pe latura dinspre trotuar sau
// alee (`laturaDeschisa`) — pe acolo vine omul. Ce se pune înăuntru e în
// metri reali (pubela de 1,1 m³ are 1,37 × 1,07 × 1,40; leagănul 2,20 m
// înălțime), iar ce nu încape nu se pune: un loc de joacă de 4 × 3 primește
// doar gardul și covorul de cauciuc.
//
// Specificațiile (`specGhena`, `specLocJoaca`) sunt geometrie pură, în cadrul
// local al dreptunghiului (x pe latura lungă), ca să se poată testa fără three;
// `construiesteZone` le transformă în plase, câte una pe material.
import * as THREE from "three";
import * as BGU from "three/examples/jsm/utils/BufferGeometryUtils.js";

export type FelZona = "playground" | "waste_platform" | "square";
export interface ZonaSit { kind: FelZona; poly: number[][]; id: string }

/** Dreptunghiul de arie minimă al unei zone: centrul, rotirea (grade, de la +x)
 *  și SEMI-laturile — `a` pe x local (latura lungă), `b` pe y local. */
export interface CadruZona { cx: number; cy: number; deg: number; a: number; b: number }

// ----------------------------------------------------------------- dimensiuni
export const PUBELA = { w: 1.37, d: 1.07, h: 1.4 };       // containerul de 1,1 m³, cu roți
export const ZID_GHENA = { h: 1.8, g: 0.12 };              // peretele platformei
export const GARD_JOACA = { h: 0.9, pas: 2.0, retras: 0.25, poarta: 1.2 };
const LEAGAN = { h: 2.2, lat: 1.6, loc: 0.8 };             // înălțimea barei, deschiderea picioarelor, pasul scaunelor
const TOBOGAN = { podest: 1.0, hPodest: 1.3, lung: 2.6, lat: 0.6 };

// ------------------------------------------------------------------ geometrie
function infasuratoare(pts: number[][]): number[][] {
  const p = [...pts].sort((u, v) => u[0] - v[0] || u[1] - v[1]);
  if (p.length < 3) return p;
  const cross = (o: number[], a: number[], b: number[]) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const jos: number[][] = [], sus: number[][] = [];
  for (const q of p) { while (jos.length >= 2 && cross(jos[jos.length - 2], jos[jos.length - 1], q) <= 0) jos.pop(); jos.push(q); }
  for (const q of p.reverse()) { while (sus.length >= 2 && cross(sus[sus.length - 2], sus[sus.length - 1], q) <= 0) sus.pop(); sus.push(q); }
  return [...jos.slice(0, -1), ...sus.slice(0, -1)];
}

/** Dreptunghiul de arie minimă: o latură a lui e pe o muchie a înfășurătorii
 *  convexe (teorema clasică), deci se încearcă fiecare muchie ca direcție. */
export function cadrulZonei(poly: number[][]): CadruZona | null {
  const hull = infasuratoare(poly.filter((p) => Number.isFinite(p?.[0]) && Number.isFinite(p?.[1])));
  if (hull.length < 3) return null;
  let best: CadruZona | null = null, arie = Infinity;
  for (let i = 0; i < hull.length; i++) {
    const p = hull[i], q = hull[(i + 1) % hull.length];
    const t = Math.atan2(q[1] - p[1], q[0] - p[0]);
    const c = Math.cos(-t), s = Math.sin(-t);
    let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
    for (const r of hull) {
      const x = r[0] * c - r[1] * s, y = r[0] * s + r[1] * c;
      x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y);
    }
    const A = (x1 - x0) * (y1 - y0);
    if (A < arie - 1e-9) {
      arie = A;
      const mx = (x0 + x1) / 2, my = (y0 + y1) / 2;
      const ct = Math.cos(t), st = Math.sin(t);
      best = { cx: mx * ct - my * st, cy: mx * st + my * ct, deg: (t * 180) / Math.PI,
               a: (x1 - x0) / 2, b: (y1 - y0) / 2 };
    }
  }
  if (!best) return null;
  // x local pe latura LUNGĂ: peretele și pubelele stau pe ea
  if (best.b > best.a) best = { ...best, deg: best.deg + 90, a: best.b, b: best.a };
  best.deg = ((best.deg + 180) % 360 + 360) % 360 - 180;
  return best;
}

/** Din cadrul local în lume. */
export function inLume(c: CadruZona, x: number, y: number): [number, number] {
  const t = (c.deg * Math.PI) / 180;
  return [c.cx + x * Math.cos(t) - y * Math.sin(t), c.cy + x * Math.sin(t) + y * Math.cos(t)];
}

/** Mijlocul laturii `k` a dreptunghiului, local: 0 = +y, 1 = +x, 2 = −y, 3 = −x. */
export function mijlocLatura(c: CadruZona, k: number): [number, number] {
  return [[0, c.b], [c.a, 0], [0, -c.b], [-c.a, 0]][k % 4] as [number, number];
}

function distLaSegment(px: number, py: number, a: number[], b: number[]): number {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const l2 = dx * dx + dy * dy;
  const t = l2 > 0 ? Math.max(0, Math.min(1, ((px - a[0]) * dx + (py - a[1]) * dy) / l2)) : 0;
  return Math.hypot(px - a[0] - t * dx, py - a[1] - t * dy);
}

function distLaInele(p: [number, number], inele: readonly number[][][]): number {
  let d = Infinity;
  for (const r of inele) for (let i = 0; i < r.length; i++) d = Math.min(d, distLaSegment(p[0], p[1], r[i], r[(i + 1) % r.length]));
  return d;
}

/** Latura dinspre care vine omul: cea al cărei mijloc e cel mai aproape de un
 *  trotuar (întâi) sau de o alee. `permise` restrânge alegerea (ghena se
 *  deschide doar pe o latură lungă, ca pubelele să stea în rând în spate). */
export function laturaDeschisa(c: CadruZona, trotuare: readonly number[][][], drumuri: readonly number[][][],
                               permise: readonly number[] = [0, 1, 2, 3]): number {
  const alege = (inele: readonly number[][][]) => {
    let best = -1, d = Infinity;
    for (const k of permise) {
      const dk = distLaInele(inLume(c, ...mijlocLatura(c, k)), inele);
      if (dk < d) { d = dk; best = k; }
    }
    return Number.isFinite(d) ? best : -1;
  };
  const t = trotuare.length ? alege(trotuare) : -1;
  if (t >= 0) return t;
  const r = drumuri.length ? alege(drumuri) : -1;
  return r >= 0 ? r : permise[0];
}

// --------------------------------------------------------------- specificații
export interface SpecGhena {
  /** Pereții, în local: capetele axei fiecăruia (trei laturi, fără cea deschisă). */
  pereti: [number, number][][];
  /** Centrul fiecărei pubele și rotirea ei (0 = cu fața spre deschidere). */
  pubele: { x: number; y: number }[];
  deschisa: number;
}

/** Ghena: perete pe trei laturi, deschiderea pe latura lungă dinspre trotuar,
 *  pubelele în rânduri lipite de peretele din spate, câte încap (pe 3 × 4 m,
 *  patru — cât cere norma pentru 1,1 m³ cu loc de manevră). */
export function specGhena(c: CadruZona, deschisa: number): SpecGhena {
  const g = ZID_GHENA.g / 2;
  const colt = (k: number): [number, number] =>
    [[c.a - g, c.b - g], [c.a - g, -c.b + g], [-c.a + g, -c.b + g], [-c.a + g, c.b - g]][k % 4] as [number, number];
  // latura k merge între colțurile k−1 și k (0: +y între (−a,+b) și (+a,+b); 1: +x …)
  const latura = (k: number): [number, number][] => [colt((k + 3) % 4), colt(k)];
  const pereti = [0, 1, 2, 3].filter((k) => k !== deschisa).map(latura);
  // deschiderea e pe o latură lungă (0 sau 2); spatele e cealaltă
  const spate = deschisa === 0 ? -1 : 1;                   // semnul lui y al peretelui din spate
  const nX = Math.max(1, Math.min(4, Math.floor((2 * c.a - 0.4) / (PUBELA.w + 0.15))));
  const nY = 2 * c.b >= 2 * PUBELA.d + ZID_GHENA.g + 0.5 ? 2 : 1;
  const pubele: { x: number; y: number }[] = [];
  const latime = nX * PUBELA.w + (nX - 1) * 0.15;
  for (let j = 0; j < nY; j++) {
    const y = spate * (c.b - ZID_GHENA.g - 0.08 - PUBELA.d / 2 - j * (PUBELA.d + 0.1));
    for (let i = 0; i < nX; i++) pubele.push({ x: -latime / 2 + PUBELA.w / 2 + i * (PUBELA.w + 0.15), y });
  }
  return { pereti, pubele, deschisa };
}

export interface SpecLocJoaca {
  stalpi: [number, number][];
  /** Șinele gardului: segmente locale (poarta lipsește). */
  sine: [number, number][][];
  poarta: number;
  leagan?: { x: number; L: number; scaune: number };
  tobogan?: { x: number };
}

/** Locul de joacă: gard jos cu poartă spre trotuar; înăuntru leagănul (de la
 *  5,5 × 3,5 m) și toboganul (de la 8,5 m lungime), așezate pe axa lungă. */
export function specLocJoaca(c: CadruZona, poarta: number): SpecLocJoaca {
  const r = GARD_JOACA.retras;
  const a = c.a - r, b = c.b - r;
  const colt: [number, number][] = [[-a, b], [a, b], [a, -b], [-a, -b]];     // latura k: colt[k] → colt[k+1]
  const stalpi: [number, number][] = [];
  const sine: [number, number][][] = [];
  for (let k = 0; k < 4; k++) {
    const p = colt[k], q = colt[(k + 1) % 4];
    const L = Math.hypot(q[0] - p[0], q[1] - p[1]);
    const n = Math.max(1, Math.ceil(L / GARD_JOACA.pas));
    const pct = (t: number): [number, number] => [p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t];
    if (k === poarta && L > GARD_JOACA.poarta + 1.0) {
      const t0 = 0.5 - GARD_JOACA.poarta / 2 / L, t1 = 0.5 + GARD_JOACA.poarta / 2 / L;
      stalpi.push(pct(t0), pct(t1));
      sine.push([p, pct(t0)], [pct(t1), q]);
      for (let i = 0; i < n; i++) { const t = i / n; if (t < t0 - 0.05 || t > t1 + 0.05) stalpi.push(pct(t)); }
    } else {
      sine.push([p, q]);
      for (let i = 0; i < n; i++) stalpi.push(pct(i / n));
    }
  }
  const spec: SpecLocJoaca = { stalpi, sine, poarta };
  if (2 * c.a >= 5.5 && 2 * c.b >= 3.5) {
    const L = Math.min(3.0, 2 * b - 1.2);
    spec.leagan = { x: -a + 0.4 + LEAGAN.lat / 2, L, scaune: L >= 2.2 ? 2 : 1 };
    if (2 * c.a >= 8.5) spec.tobogan = { x: a - 0.5 - TOBOGAN.podest / 2 };
  }
  return spec;
}

// ---------------------------------------------------------------------- three
function cutie(w: number, d: number, h: number, x: number, y: number, z: number): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, d, h);
  g.translate(x, y, z + h / 2);
  return g;
}

/** Un cilindru între două puncte (stâlp, bară, frânghie, picior înclinat). */
function tija(r: number, de: [number, number, number], la: [number, number, number]): THREE.BufferGeometry {
  const a = new THREE.Vector3(...de), b = new THREE.Vector3(...la);
  const d = b.clone().sub(a);
  const g = new THREE.CylinderGeometry(r, r, d.length(), 8);
  g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.clone().normalize()));
  g.translate((a.x + b.x) / 2, (a.y + b.y) / 2, (a.z + b.z) / 2);
  return g;
}

const CULORI_PUBELE = [0x2e6b3f, 0x2a5d8f, 0xc9a227, 0x55585c];   // colectare selectivă, pe rând

/** Plasele zonelor, câte un grup pe zonă, așezat pe cadrul ei. `deArs` ține
 *  geometriile și materialele. */
export function construiesteZone(zone: readonly ZonaSit[], drumuri: readonly { poly: number[][] }[],
                                 trotuare: readonly { poly: number[][] }[]) {
  const obiecte: THREE.Object3D[] = [];
  const deArs: { dispose(): void }[] = [];
  if (!zone.length) return { obiecte, deArs };
  const mat = {
    zid: new THREE.MeshStandardMaterial({ color: 0xcfcac0, roughness: 1 }),
    capac: new THREE.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.7 }),
    roata: new THREE.MeshStandardMaterial({ color: 0x1d1d1f, roughness: 0.9 }),
    pubele: CULORI_PUBELE.map((c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.6 })),
    gard: new THREE.MeshStandardMaterial({ color: 0x6b4f2a, roughness: 0.9 }),      // lemn
    metal: new THREE.MeshStandardMaterial({ color: 0x2c6fb0, roughness: 0.5, metalness: 0.3 }),
    galben: new THREE.MeshStandardMaterial({ color: 0xf2b233, roughness: 0.6 }),
    scaun: new THREE.MeshStandardMaterial({ color: 0x2b2b2b, roughness: 0.8 }),
    franghie: new THREE.MeshStandardMaterial({ color: 0x9a9a9a, roughness: 1 }),
  };
  deArs.push(mat.zid, mat.capac, mat.roata, ...mat.pubele, mat.gard, mat.metal, mat.galben, mat.scaun, mat.franghie);
  const inele = (l: readonly { poly: number[][] }[]) => l.map((s) => s.poly);

  const adauga = (grup: THREE.Group, geos: THREE.BufferGeometry[], m: THREE.Material) => {
    if (!geos.length) return;
    const g = BGU.mergeGeometries(geos, false);
    geos.forEach((x) => x.dispose());
    if (!g) return;
    deArs.push(g);
    const mesh = new THREE.Mesh(g, m);
    mesh.castShadow = true; mesh.receiveShadow = true;
    grup.add(mesh);
  };

  for (const z of zone) {
    if (z.kind === "square") continue;                       // piațeta e doar pavaj (v. RandareSit3D)
    const c = cadrulZonei(z.poly);
    if (!c || c.a < 0.8 || c.b < 0.8) continue;
    const grup = new THREE.Group();
    grup.name = `zona:${z.id}`;
    grup.position.set(c.cx, c.cy, 0);
    grup.rotation.z = (c.deg * Math.PI) / 180;
    if (z.kind === "waste_platform") {
      const sp = specGhena(c, laturaDeschisa(c, inele(trotuare), inele(drumuri), [0, 2]));
      adauga(grup, sp.pereti.map(([p, q]) => {
        const L = Math.hypot(q[0] - p[0], q[1] - p[1]);
        const g = new THREE.BoxGeometry(L + ZID_GHENA.g, ZID_GHENA.g, ZID_GHENA.h);
        g.rotateZ(Math.atan2(q[1] - p[1], q[0] - p[0]));
        g.translate((p[0] + q[0]) / 2, (p[1] + q[1]) / 2, ZID_GHENA.h / 2);
        return g;
      }), mat.zid);
      sp.pubele.forEach((pb, i) => {
        adauga(grup, [cutie(PUBELA.w, PUBELA.d, PUBELA.h - 0.2, pb.x, pb.y, 0.15)], mat.pubele[i % mat.pubele.length]);
        adauga(grup, [cutie(PUBELA.w + 0.04, PUBELA.d + 0.04, 0.1, pb.x, pb.y, PUBELA.h - 0.05)], mat.capac);
        const roti: THREE.BufferGeometry[] = [];
        for (const sx of [-1, 1]) for (const sy of [-1, 1]) {
          const r = new THREE.CylinderGeometry(0.1, 0.1, 0.05, 10);
          r.rotateZ(Math.PI / 2);
          r.translate(pb.x + sx * (PUBELA.w / 2 - 0.15), pb.y + sy * (PUBELA.d / 2 - 0.1), 0.1);
          roti.push(r);
        }
        adauga(grup, roti, mat.roata);
      });
    } else {
      const sp = specLocJoaca(c, laturaDeschisa(c, inele(trotuare), inele(drumuri)));
      const lemn: THREE.BufferGeometry[] = sp.stalpi.map(([x, y]) => cutie(0.09, 0.09, GARD_JOACA.h, x, y, 0));
      for (const [p, q] of sp.sine) for (const h of [0.42, GARD_JOACA.h - 0.08]) {
        const L = Math.hypot(q[0] - p[0], q[1] - p[1]);
        const g = new THREE.BoxGeometry(L, 0.04, 0.08);
        g.rotateZ(Math.atan2(q[1] - p[1], q[0] - p[0]));
        g.translate((p[0] + q[0]) / 2, (p[1] + q[1]) / 2, h);
        lemn.push(g);
      }
      adauga(grup, lemn, mat.gard);
      const metal: THREE.BufferGeometry[] = [], scaune: THREE.BufferGeometry[] = [], franghii: THREE.BufferGeometry[] = [];
      if (sp.leagan) {
        const { x, L, scaune: n } = sp.leagan;
        const y0 = -L / 2, y1 = L / 2;
        metal.push(tija(0.05, [x, y0, LEAGAN.h], [x, y1, LEAGAN.h]));
        for (const y of [y0, y1]) for (const s of [-1, 1]) metal.push(tija(0.05, [x + s * LEAGAN.lat / 2, y, 0], [x, y, LEAGAN.h]));
        for (let i = 0; i < n; i++) {
          const y = n === 1 ? 0 : -LEAGAN.loc / 2 + i * LEAGAN.loc;
          scaune.push(cutie(0.2, 0.45, 0.04, x, y, 0.5));
          for (const s of [-1, 1]) franghii.push(tija(0.012, [x, y + s * 0.2, 0.54], [x, y + s * 0.2, LEAGAN.h]));
        }
      }
      if (sp.tobogan) {
        const { x } = sp.tobogan, P = TOBOGAN.podest, H = TOBOGAN.hPodest;
        metal.push(cutie(P, P, 0.06, x, 0, H));
        for (const sx of [-1, 1]) for (const sy of [-1, 1]) metal.push(tija(0.04, [x + sx * (P / 2 - 0.05), sy * (P / 2 - 0.05), 0], [x + sx * (P / 2 - 0.05), sy * (P / 2 - 0.05), H + 0.9]));
        for (const sy of [-1, 1]) metal.push(tija(0.025, [x - P / 2, sy * (P / 2 - 0.05), H + 0.9], [x + P / 2, sy * (P / 2 - 0.05), H + 0.9]));
        // scara pe +x: două lonjeroane înclinate și trepte
        const xs = x + P / 2, xj = xs + 0.6;
        for (const sy of [-1, 1]) metal.push(tija(0.03, [xj, sy * 0.25, 0], [xs, sy * 0.25, H]));
        for (let i = 1; i <= 4; i++) { const t = i / 5; metal.push(tija(0.02, [xj - 0.6 * t, -0.25, H * t], [xj - 0.6 * t, 0.25, H * t])); }
        // jgheabul pe −x, de la podest până la 0,30 de sol. Rotirea în jurul lui y
        // coboară capătul de la +x (podestul) cu unghi pozitiv, deci se dă cu minus:
        // cu plus, jgheabul zăcea pe sol și urca spre capătul liber (Oana, 02.11.2026)
        const l = Math.hypot(TOBOGAN.lung, H - 0.3), ang = -Math.atan2(H - 0.3, TOBOGAN.lung);
        const j = new THREE.BoxGeometry(l, TOBOGAN.lat, 0.06);
        j.rotateY(ang);
        j.translate(x - P / 2 - TOBOGAN.lung / 2, 0, (H + 0.3) / 2);
        const jgheab = [j];
        for (const s of [-1, 1]) {
          const m = new THREE.BoxGeometry(l, 0.04, 0.18);
          m.rotateY(ang);
          m.translate(x - P / 2 - TOBOGAN.lung / 2, s * (TOBOGAN.lat / 2 - 0.02), (H + 0.3) / 2 + 0.09);
          metal.push(m);
        }
        adauga(grup, jgheab, mat.galben);
      }
      adauga(grup, metal, mat.metal);
      adauga(grup, scaune, mat.scaun);
      adauga(grup, franghii, mat.franghie);
    }
    obiecte.push(grup);
  }
  return { obiecte, deArs };
}
